// Dünner API-Client zum Spyship-Server. Auth = Cookie-Session (BFF), daher
// `credentials: "include"`; kein Access-Token im JS (docs/auth.md). Ein 401
// wird hier NICHT in einen Redirect umgemünzt — das würde laufende Formulare
// wegwerfen und fetch-Ergebnisse verfälschen. Stattdessen meldet der Client den
// Verlust der Session einmal zentral (`onUnauthenticated`), und die UI zeigt
// die Login-Ansicht.

import type {
  AuthUser,
  Backup,
  BlocklistOverview,
  BootstrapToken,
  BootstrapTokenCreated,
  Capture,
  Device,
  DeviceDetail,
  Enrollment,
  Firmware,
  FirmwareProgress,
  FlashSchedule,
  Job,
  LogsPullResult,
  Model,
  ModelInput,
  SystemStatus,
  WgOverview,
  WgPeerInput,
  WgServerConfig,
} from "./api-types";

// Die Kontrakt-Typen liegen in `api-types.ts` — hier re-exportiert, damit
// `lib/api` der eine Import-Ort für Transport *und* Typen bleibt.
export type * from "./api-types";

/**
 * HTTP-Fehler mit Status + geparstem Body. Wichtig für die Detailseite:
 * `503` heißt „Router offline/Timeout“ — der Cache bleibt gültig und die UI
 * zeigt nur einen dezenten Hinweis statt eines roten Totalausfalls.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: { error?: string; [k: string]: unknown };

  constructor(status: number, body: { error?: string; [k: string]: unknown }) {
    super(body.error ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  /** Router nicht erreichbar (offline oder RPC-Timeout). */
  get isOffline(): boolean {
    return this.status === 503;
  }
}

/** Fehlertext für die UI — ohne „Error: “-Präfix-Salat. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.body.error ?? e.message;
  return e instanceof Error ? e.message : String(e);
}

/**
 * Wird bei jedem 401 gerufen — genau ein Abnehmer: der Auth-Zustand in
 * `lib/hooks/useAuth`. Registrierung statt Import, damit der Transport nichts
 * über React weiß.
 */
let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

/** Cookie-Wert lesen. Nur für `csrftoken` — das Session-Cookie ist httpOnly. */
function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Schreibende Methoden brauchen Djangos CSRF-Header (docs/auth.md → CSRF). */
const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

type Init = Omit<RequestInit, "headers"> & { headers?: Record<string, string> };

async function req<T>(path: string, init?: Init): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const csrfToken = WRITE_METHODS.has(method) ? readCookie("csrftoken") : null;
  // Bei FormData MUSS der Browser den content-type setzen — nur er kennt die
  // multipart-Boundary. Ein von Hand gesetzter Header macht den Body unlesbar.
  const isForm = init?.body instanceof FormData;
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    ...init,
    headers: {
      ...(isForm ? {} : { "content-type": "application/json" }),
      // Das Cookie setzt der Server bei `GET /api/auth/me`; fehlt es, schickt
      // der Server ohnehin 403 — dann ist der Header auch nicht zu retten.
      ...(csrfToken ? { "X-CSRFToken": csrfToken } : {}),
      ...init?.headers,
    },
  });
  if (res.status === 401) onUnauthenticated?.();
  if (!res.ok) {
    let body: { error?: string; [k: string]: unknown } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      body = { error: `${res.status} ${res.statusText}` };
    }
    throw new ApiError(res.status, body);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const api = {
  // --- Auth (docs/auth.md) ---
  /** Angemeldeten Nutzer holen; setzt nebenbei das `csrftoken`-Cookie. */
  getCurrentUser: () => req<AuthUser>("/auth/me"),
  /** Session verwerfen. Die Abmeldung bei Keycloak läuft über `logoutUrl`. */
  logout: () => req<{ logoutUrl: string }>("/auth/logout", { method: "POST" }),
  /** Einstieg in den Code-Flow — echte Navigation, niemals per fetch. */
  loginUrl: (next: string) => `/api/auth/login?next=${encodeURIComponent(next)}`,

  /** Ablaufende Zertifikate und anderes, das leise kaputtgeht. */
  getSystemStatus: () => req<SystemStatus>("/system"),

  listDevices: () => req<Device[]>("/devices"),
  /** Detail + gecachte Sektionen. Kontaktiert den Router NICHT. */
  getDevice: (id: string) => req<DeviceDetail>(`/devices/${id}`),
  /** Aus der Flotte nehmen — der Router selbst bleibt unberührt (docs/device-management.md). */
  deleteDevice: (id: string) => req<void>(`/devices/${id}`, { method: "DELETE" }),

  /** Genau eine Sektion live abfragen — kostet Mobilfunkvolumen, nur auf Klick. */
  refreshSection: (id: string, section: string) =>
    req<{ section: string; data: unknown; fetchedAt: string }>(
      `/devices/${id}/sections/${section}/refresh`,
      { method: "POST" },
    ),

  /** Fortschritt des laufenden Firmware-Downloads (der Server serviert die
   *  Bytes und zählt sie). Leeres Objekt = kein Download aktiv. */
  firmwareProgress: (id: string) => req<FirmwareProgress>(`/devices/${id}/firmware-progress`),

  // --- Geplante Updates (der Server flasht, nicht der Browser) ---
  listFlashSchedules: (deviceId?: string) =>
    req<FlashSchedule[]>(
      `/flash-schedules${deviceId ? `?device=${encodeURIComponent(deviceId)}` : ""}`,
    ),
  /** `runAt` ist ein ISO-Zeitpunkt MIT Zone — sonst rät der Server. */
  scheduleFlash: (id: string, firmwareId: string, runAt: string) =>
    req<FlashSchedule>(`/devices/${id}/flash-schedule`, {
      method: "POST",
      body: JSON.stringify({ firmwareId, runAt }),
    }),
  /** Verschieben statt absagen-und-neu: der Termin behält seine ID. */
  rescheduleFlash: (scheduleId: string, runAt: string) =>
    req<FlashSchedule>(`/flash-schedules/${scheduleId}`, {
      method: "PATCH",
      body: JSON.stringify({ runAt }),
    }),
  cancelFlashSchedule: (scheduleId: string) =>
    req<void>(`/flash-schedules/${scheduleId}`, { method: "DELETE" }),

  /**
   * Aktion aus der Allowlist auslösen. `params` sind die flachen Wire-Params
   * aus docs/wire-protocol-v1.md (z.B. `confirm_token`), der Server reicht sie
   * durch. 503 → Router offline (ApiError.isOffline).
   */
  runAction: <T = unknown>(id: string, action: string, params?: Record<string, unknown>) =>
    req<{ ok: boolean; data: T; jobId: string }>(`/devices/${id}/actions/${action}`, {
      method: "POST",
      body: JSON.stringify(params ?? {}),
    }),

  // Bequeme Wrapper auf runAction (kein eigener Endpunkt — ein Kontrakt).
  reboot: (id: string) => api.runAction<{ rebooting: boolean }>(id, "reboot"),
  pullLogs: (id: string, lines = 500) => api.runAction<LogsPullResult>(id, "logs_pull", { lines }),

  // Live-Log-Stream: SSE vom Server (der den Device-WS anzapft). Ein Event kann
  // MEHRERE Zeilen tragen (der Agent bündelt ~1 s, spart Volumen).
  streamLogs(id: string, onChunk: (chunk: string) => void, onError?: () => void): () => void {
    const es = new EventSource(`/events/devices/${id}/logs`, { withCredentials: true });
    es.onmessage = (e: MessageEvent<string>) => onChunk(e.data);
    if (onError) es.onerror = () => onError();
    return () => es.close();
  },

  // --- Backups (Katalog auf dem Server, nicht im Browser) ---
  listBackups: (deviceId?: string) =>
    req<Backup[]>(`/backups${deviceId ? `?device=${encodeURIComponent(deviceId)}` : ""}`),
  /** Backup auf einen Router einspielen — der Router lädt das Tar selbst. */
  restoreBackup: (backupId: string, deviceId: string) =>
    req<{ ok: boolean; jobId?: string }>(`/backups/${backupId}/restore`, {
      method: "POST",
      body: JSON.stringify({ deviceId }),
    }),
  deleteBackup: (backupId: string) => req<void>(`/backups/${backupId}`, { method: "DELETE" }),
  /** Direktlink — der Server liefert die entschlüsselten Bytes aus, kein fetch(). */
  backupDownloadUrl: (backupId: string) => `/api/backups/${backupId}/download`,
  /** Sammel-Export: ZIP mit dem letzten Backup jedes Routers, entschlüsselt. */
  backupExportUrl: "/api/backups/export",

  // --- Mitschnitte (tcpdump; Katalog auf dem Server) ---
  listCaptures: (deviceId?: string) =>
    req<Capture[]>(`/captures${deviceId ? `?device=${encodeURIComponent(deviceId)}` : ""}`),
  /**
   * Mitschnitt starten. Antwortet, sobald tcpdump läuft — die Datei kommt
   * später (der Router lädt sie hoch). `409` = auf dem Router läuft schon einer.
   */
  startCapture: (id: string, params: Record<string, unknown>) =>
    api.runAction<{ capturing: boolean; iface: string }>(id, "capture_start", params),
  /** Vorzeitig beenden. Der Upload läuft danach noch — „gestoppt" ≠ „fertig". */
  stopCapture: (id: string) => api.runAction<{ stopped: boolean }>(id, "capture_stop"),
  deleteCapture: (captureId: string) => req<void>(`/captures/${captureId}`, { method: "DELETE" }),
  /** Direktlink — der Server liefert die entschlüsselte `.pcap.gz` aus. */
  captureDownloadUrl: (captureId: string) => `/api/captures/${captureId}/download`,

  // --- Audit: Kommando-/Job-Historie ---
  listJobs: (deviceId?: string) =>
    req<Job[]>(`/jobs${deviceId ? `?device=${encodeURIComponent(deviceId)}` : ""}`),

  // --- Modelle + Firmware-Katalog ---
  listModels: () => req<Model[]>("/models"),
  createModel: (m: ModelInput) =>
    req<{ id: string }>("/models", { method: "POST", body: JSON.stringify(m) }),
  updateModel: (id: string, m: ModelInput) =>
    req<{ ok: true }>(`/models/${id}`, { method: "PUT", body: JSON.stringify(m) }),
  deleteModel: (id: string) => req<void>(`/models/${id}`, { method: "DELETE" }),
  listFirmware: () => req<Firmware[]>("/firmware"),
  deleteFirmware: (id: string) => req<void>(`/firmware/${id}`, { method: "DELETE" }),

  /** Image hochladen. Der Server liest Version und Gerät aus dem Image selbst;
   *  `version` zählt nur bei Images ohne OpenWrt-Metadaten, `force` übergeht
   *  einen erkannten Modell-Konflikt. */
  uploadFirmware(
    file: File,
    modelId: string,
    opts: { version?: string; tag?: string; force?: boolean; signature?: File | null } = {},
  ): Promise<Firmware> {
    const form = new FormData();
    form.append("file", file);
    form.append("modelId", modelId);
    if (opts.version) form.append("version", opts.version);
    if (opts.tag) form.append("tag", opts.tag);
    if (opts.force) form.append("force", "true");
    // Die usign-.sig (aus dem Image-Build). Ohne sie lehnt der Router das
    // Flashen ab — die einzige Aktion ohne Rollback.
    if (opts.signature) form.append("signature", opts.signature);
    return req<Firmware>("/firmware", { method: "POST", body: form });
  },

  // --- Enrollment-Freigaben ---
  listEnrollments: (status = "pending") => req<Enrollment[]>(`/enrollments?status=${status}`),
  /** `force` bestätigt die Übernahme einer bereits vergebenen Geräte-ID. */
  approveEnrollment: (id: string, force = false) =>
    req<{ ok: true }>(`/enrollments/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ force }),
    }),
  rejectEnrollment: (id: string) =>
    req<{ ok: true }>(`/enrollments/${id}/reject`, { method: "POST" }),

  // --- WireGuard-Konzentrator (docs/wireguard-server.md) ---
  /**
   * Soll und Ist des Servers in EINER Antwort. Weckt keinen Router: gelesen
   * werden die Datenbank und die Statusdatei des lokalen Sync-Dienstes.
   */
  getWireguard: () => req<WgOverview>("/wireguard"),
  updateWgServer: (patch: Partial<WgServerConfig>) =>
    req<WgOverview>("/wireguard", { method: "PATCH", body: JSON.stringify(patch) }),
  createWgPeer: (peer: WgPeerInput) =>
    req<WgOverview>("/wireguard/peers", { method: "POST", body: JSON.stringify(peer) }),
  updateWgPeer: (id: string, peer: WgPeerInput) =>
    req<WgOverview>(`/wireguard/peers/${id}`, { method: "PATCH", body: JSON.stringify(peer) }),
  /** Peer entfernen — der Zugang ist damit sofort zu. */
  deleteWgPeer: (id: string) => req<WgOverview>(`/wireguard/peers/${id}`, { method: "DELETE" }),
  /**
   * Konzentrator live mitlesen. Der Server schickt nur bei echten Änderungen —
   * kein Intervall im Browser, kein F5. Rückgabe: abbestellen.
   */
  streamWireguard(onData: (data: WgOverview) => void, onError?: () => void): () => void {
    const es = new EventSource("/events/wireguard", { withCredentials: true });
    es.addEventListener("wireguard", (e: MessageEvent<string>) => {
      onData(JSON.parse(e.data) as WgOverview);
    });
    if (onError) es.onerror = () => onError();
    return () => es.close();
  },

  // --- Zentrale MAC-Blockliste (docs/device-management.md) ---
  /** Bestand + Anwendungsstand je Router. Weckt keinen Router. */
  getBlocklist: () => req<BlocklistOverview>("/blocklist"),
  /** Eintrag aufnehmen (MAC oder OUI) — der Server pusht sofort auf die Flotte. */
  addBlocklistEntry: (mac: string, comment?: string) =>
    req<BlocklistOverview>("/blocklist/entries", {
      method: "POST",
      body: JSON.stringify({ mac, comment }),
    }),
  /** Eintrag entfernen — auch das wird sofort gepusht. */
  deleteBlocklistEntry: (id: string) =>
    req<BlocklistOverview>(`/blocklist/entries/${id}`, { method: "DELETE" }),

  // --- Enrollment-/Bootstrap-Token ---
  listBootstrapTokens: () => req<BootstrapToken[]>("/bootstrap-tokens"),
  createBootstrapToken: (body: { label: string; maxUses?: number; days?: number }) =>
    req<BootstrapTokenCreated>("/bootstrap-tokens", { method: "POST", body: JSON.stringify(body) }),
  revokeBootstrapToken: (id: string) =>
    req<BootstrapToken>(`/bootstrap-tokens/${id}/revoke`, { method: "POST" }),
};
